/**
 * supabase/misc.js — Dominios varios: blacklist wa_excluidos, analytics
 * (v_analytics_resumen), campus, stubs de marketing y workflow_config
 * (toggles de IA por instancia + realtime). Parte de la fachada
 * src/lib/supabase.js.
 */

import { getSupabase, supabase } from "./client";
import { deleteLead } from "./leads";

// ═══════════════════════════════════════════════════════════════════════
// BLACKLIST (wa_excluidos) — contactos que NO deben crear lead
// ═══════════════════════════════════════════════════════════════════════

/**
 * Marca un contacto como "no es prospecto" y lo saca permanentemente del CRM.
 *
 * 1. Inserta una fila en `wa_excluidos` (sobrevive al delete del lead, así
 *    cuando esa persona vuelva a escribir, n8n consulta esta tabla y NO
 *    crea un lead nuevo).
 * 2. Elimina el lead y su memoria de chat (igual que `deleteLead`).
 *
 * @param {object} params
 * @param {string} params.leadId
 * @param {string} [params.motivo]  ej. "no_prospecto", "vendedor", "equivocacion", "familiar"
 * @param {string} [params.notas]   texto libre opcional
 * @param {string} [params.excluidoPor]  email del usuario que lo marcó
 */
export async function excludeWaContact({ leadId, motivo = "no_prospecto", notas = null, excluidoPor = null }) {
  const client = getSupabase();

  const { data: lead, error: readErr } = await client
    .from("leads")
    .select("wa_jid, telefono, nombre_tutor, instancia")
    .eq("id", leadId)
    .maybeSingle();

  if (readErr || !lead) {
    return { error: readErr ?? new Error("Lead no encontrado") };
  }
  if (!lead.wa_jid) {
    return { error: new Error("El lead no tiene wa_jid (no se puede excluir por WhatsApp)") };
  }

  const { error: upsertErr } = await client.from("wa_excluidos").upsert(
    {
      wa_jid: lead.wa_jid,
      telefono: lead.telefono ?? null,
      nombre_capturado: lead.nombre_tutor ?? null,
      motivo,
      notas,
      excluido_por: excluidoPor,
      instancia: lead.instancia ?? null,
    },
    { onConflict: "wa_jid" },
  );

  if (upsertErr) return { error: upsertErr };

  // Reutilizamos deleteLead para limpiar lead + memoria del bot + mensajes huérfanos
  return await deleteLead(leadId);
}

/** Quita un wa_jid de la blacklist (para casos en que se equivocaron). */
export async function removeFromExclusion(waJid) {
  return await getSupabase().from("wa_excluidos").delete().eq("wa_jid", waJid);
}

/** Lista de contactos excluidos. */
export async function listExclusions() {
  return await getSupabase()
    .from("wa_excluidos")
    .select("*")
    .order("created_at", { ascending: false });
}

/** Verifica si un wa_jid está en la blacklist. */
export async function isWaExcluded(waJid) {
  if (!waJid) return false;
  const { data } = await getSupabase()
    .from("wa_excluidos")
    .select("wa_jid")
    .eq("wa_jid", waJid)
    .maybeSingle();
  return Boolean(data);
}

// ═══════════════════════════════════════════════════
// ANALYTICS — Usa VIEW v_analytics_resumen (server-side)
// ═══════════════════════════════════════════════════

/**
 * Obtiene analytics globales.
 *
 * Retorna conteos server-side (v_analytics_resumen) + array de leads
 * completos (v_leads_completos) porque el AdminPage necesita el array
 * para calcular filtros por campus, ranking de asesores, etc.
 *
 * Nota: cuando el volumen supere ~5000 leads, refactorear AdminPage
 * para usar las views/RPCs directamente y no depender del array.
 *
 * @returns {Promise<{data: Object|null, error: any}>}
 */
export async function getAnalytics() {
  const client = getSupabase();

  // Ejecutar ambas queries en paralelo
  const [statsResult, leadsResult] = await Promise.all([
    client.from("v_analytics_resumen").select("*").single(),
    client
      .from("v_leads_completos")
      .select("*")
      .order("created_at", { ascending: false }),
  ]);

  if (statsResult.error) {
    console.warn(
      "[supabase] getAnalytics stats error:",
      statsResult.error.message,
    );
  }
  if (leadsResult.error) {
    console.warn(
      "[supabase] getAnalytics leads error:",
      leadsResult.error.message,
    );
  }

  const stats = statsResult.data;
  const rawLeads = leadsResult.data ?? [];

  // Normalizar leads para compat (campus como string)
  const leads = rawLeads.map((row) => ({
    ...row,
    campus: row.campus_nombre ?? row.campus ?? row.campus_id ?? null,
    campus_codigo: row.campus_codigo ?? null,
    campus_estado: row.campus_estado ?? null,
    campus_municipio: row.campus_municipio ?? null,
    asesor_email: row.asesor_email ?? null,
    asesor_nombre: row.asesor_nombre ?? null,
  }));

  return {
    data: {
      // Array completo de leads (para AdminPage: filtros, ranking, gráficas)
      leads,
      // Conteos server-side (para uso directo sin recalcular)
      totalLeads: stats?.total_leads ?? leads.length,
      leadsByStatus: {
        nuevo: stats?.nuevos ?? 0,
        contactado: stats?.contactados ?? 0,
        cita_solicitada: stats?.cita_solicitada ?? 0,
        cita: stats?.con_cita ?? 0,
        interesado: stats?.interesados ?? 0,
        cierre: stats?.en_cierre ?? 0,
        inscrito: stats?.inscritos ?? 0,
        perdido: stats?.perdidos ?? 0,
        spam: stats?.spam ?? 0,
      },
      leadsByQuality: {
        caliente: stats?.leads_calientes ?? 0,
        tibio: stats?.leads_tibios ?? 0,
        frio: stats?.leads_frios ?? 0,
      },
      conversionRate: stats?.tasa_conversion_pct ?? 0,
    },
    error: statsResult.error || leadsResult.error || null,
  };
}

// ═══════════════════════════════════════════════════
// CAMPUS
// ═══════════════════════════════════════════════════

/** Obtiene campus actual de localStorage */
export const getCurrentCampus = () =>
  localStorage.getItem("current_campus") || "default";

/** Setea campus actual */
export function setCurrentCampus(campusId) {
  if (campusId) localStorage.setItem("current_campus", campusId);
}

/** Lista campus. Usa: TABLE campus */
export async function getAllCampus({ includeInactive = false } = {}) {
  let query = getSupabase().from("campus").select("*").order("nombre");
  if (!includeInactive) query = query.eq("activo", true);
  const { data, error } = await query;
  return { data: data ?? [], error };
}

// ═══════════════════════════════════════════════════
// MARKETING (stub — retorna vacío para compat)
// ═══════════════════════════════════════════════════

/** @returns {Promise<{data: Array, error: null}>} */
export async function getMarketingCampaigns() {
  return { data: [], error: null };
}

/** @returns {Promise<{data: Array, error: null}>} */
export async function getCopilotMetricsContext() {
  return { data: [], error: null };
}

// ═══════════════════════════════════════════════════
// WORKFLOW CONFIG (toggle IA por instancia/workflow en n8n)
// ═══════════════════════════════════════════════════

// Mapeo instancia Evolution → key en workflow_config.
// Cada workflow n8n lee SU key específica, así podemos prender/apagar la IA por instancia.
export const INSTANCE_TO_IA_KEY = {
  "tepeyact-produccion": "ia_pausada_lulu",
  "tepeyec-gaby": "ia_pausada_gaby",
  "brain": "ia_pausada_yair",
};

export const IA_KEYS = Object.values(INSTANCE_TO_IA_KEY);

/** Lee si la IA está pausada para una instancia concreta. */
export async function getIaPausadaForInstance(instanceName) {
  const key = INSTANCE_TO_IA_KEY[instanceName];
  if (!key) return null; // instancia desconocida
  const { data, error } = await supabase
    .from("workflow_config")
    .select("value")
    .eq("key", key)
    .maybeSingle();
  if (error) {
    console.warn(`[getIaPausadaForInstance ${key}]`, error.message);
    return false;
  }
  return String(data?.value || "").toLowerCase() === "true";
}

/** Cambia el toggle de IA para UNA instancia. Verifica que se persista. */
export async function setIaPausadaForInstance(instanceName, pausada) {
  const key = INSTANCE_TO_IA_KEY[instanceName];
  if (!key) throw new Error(`Instancia desconocida: ${instanceName}`);
  const value = pausada ? "true" : "false";
  const { error } = await supabase
    .from("workflow_config")
    .update({ value, updated_at: new Date().toISOString() })
    .eq("key", key);
  if (error) throw error;
  const { data: check } = await supabase
    .from("workflow_config")
    .select("value")
    .eq("key", key)
    .maybeSingle();
  const applied = String(check?.value || "").toLowerCase() === "true";
  if (applied !== pausada) {
    throw new Error(`El cambio NO se persistió en ${key} (falta RLS UPDATE policy en workflow_config).`);
  }
  return pausada;
}

/** Lee el estado de IA de TODAS las instancias. Devuelve { lulu, gaby, yair }. */
export async function getIaPausadaAll() {
  const { data, error } = await supabase
    .from("workflow_config")
    .select("key, value")
    .in("key", IA_KEYS);
  if (error) {
    console.warn("[getIaPausadaAll]", error.message);
    return {};
  }
  const out = {};
  for (const row of data || []) {
    out[row.key] = String(row.value || "").toLowerCase() === "true";
  }
  return out;
}

/** Cambia TODAS las keys IA (pausar/reactivar globalmente). */
export async function setIaPausadaAll(pausada) {
  const value = pausada ? "true" : "false";
  const { error } = await supabase
    .from("workflow_config")
    .update({ value, updated_at: new Date().toISOString() })
    .in("key", IA_KEYS);
  if (error) throw error;
  // Verificar que las 3 se aplicaron
  const verif = await getIaPausadaAll();
  const todasIguales = IA_KEYS.every(k => verif[k] === pausada);
  if (!todasIguales) {
    throw new Error("Algunas keys no se actualizaron. Verifica RLS UPDATE policy en workflow_config.");
  }
  return pausada;
}

/** Realtime subscription a CUALQUIER cambio en las keys de IA. cb recibe el mapa completo. */
export function subscribeToIaPausadaAll(cb) {
  const channel = supabase
    .channel("workflow_config:ia_pausada_all")
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "workflow_config" },
      async (payload) => {
        if (!IA_KEYS.includes(payload?.new?.key)) return;
        const fresh = await getIaPausadaAll();
        cb(fresh);
      },
    )
    .subscribe();
  return () => supabase.removeChannel(channel);
}

// ───────────────────────────────────────────────────
// Legacy compat: getIaPausada/setIaPausada usan la antigua key "ia_pausada"
// Mantenidas para no romper código que aún no migra. NO debe usarse en código nuevo.
// ───────────────────────────────────────────────────

/**
 * @deprecated usa getIaPausadaAll() o getIaPausadaForInstance(name)
 * Devuelve true si TODAS las instancias están pausadas. Si hay mixto, devuelve false.
 */
export async function getIaPausada() {
  const all = await getIaPausadaAll();
  const vals = Object.values(all);
  if (!vals.length) return false;
  return vals.every(v => v === true);
}

/**
 * @deprecated usa setIaPausadaAll() o setIaPausadaForInstance(name, value)
 * Setea TODAS las instancias al mismo valor.
 */
export async function setIaPausada(pausada) {
  return setIaPausadaAll(pausada);
}

/** @deprecated usa subscribeToIaPausadaAll */
export function subscribeToIaPausada(cb) {
  return subscribeToIaPausadaAll((all) => {
    const vals = Object.values(all);
    const todasPausadas = vals.length > 0 && vals.every(v => v === true);
    cb(todasPausadas);
  });
}
